import { createFileRoute, redirect, Link } from '@tanstack/react-router'
import { useAuthStore } from '@/store/auth.store'
import {
  ShoppingBag, Users, BarChart3, Zap, MapPin, Package,
  ArrowRight, CheckCircle2, Smartphone, ClipboardList, TrendingUp,
} from 'lucide-react'


export const Route = createFileRoute('/')({
  beforeLoad: () => {
    const { accessToken, activeBusiness } = useAuthStore.getState()
    if (accessToken) {
      if (activeBusiness?.portal_only) throw redirect({ to: '/employee' })
      if (activeBusiness?.is_owner) throw redirect({ to: '/dashboard' })
      throw redirect({ to: '/sales', search: { tab: 'sales' } })
    }
  },
  component: LandingPage,
})

function LandingPage() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Nav />
      <Hero />
      <Products />
      <HowItWorks />
      <Features />
      <DistriMarketSpotlight />
      <Industries />
      <CTA />
      <Footer />
    </div>
  )
}

function Nav() {
  return (
    <header className="sticky top-0 z-50 border-b border-gray-100 bg-white/80 backdrop-blur">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="DistriHub" className="w-8 h-8 object-contain" />
          <span className="text-lg font-bold tracking-tight">Distri<span className="text-rose-600">Hub</span></span>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            className="text-sm text-gray-600 hover:text-gray-900 font-medium px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Sign in
          </Link>
          <Link
            to="/register"
            className="text-sm font-medium bg-rose-600 text-white px-4 py-1.5 rounded-lg hover:bg-rose-700 transition-colors"
          >
            Get started free
          </Link>
        </div>
      </div>
    </header>
  )
}

function Hero() {
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-20 pb-16 text-center">
      <div className="inline-flex items-center gap-2 bg-rose-50 text-rose-700 text-xs font-semibold px-3 py-1 rounded-full mb-6">
        Built for Kenyan businesses
      </div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 leading-tight">
        From your warehouse to your last-mile agent.<br />
        <span className="text-rose-600">One platform runs it all.</span>
      </h1>
      <p className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
        Distri<span className="text-rose-600">Hub</span> gives distributors, retailers, and their field teams
        everything they need: inventory, sales, payroll, field agents, and a consumer marketplace.
      </p>
      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Link
          to="/register"
          className="flex items-center gap-2 bg-rose-600 text-white font-semibold px-6 py-3 rounded-xl hover:bg-rose-700 transition-colors text-sm"
        >
          Start for free
          <ArrowRight size={16} />
        </Link>
        <Link
          to="/login"
          className="flex items-center gap-2 border border-gray-200 text-gray-700 font-medium px-6 py-3 rounded-xl hover:bg-gray-50 transition-colors text-sm"
        >
          Sign in to your ERP
        </Link>
      </div>
      <p className="mt-4 text-xs text-gray-500">Free to start. No credit card. No contract.</p>
    </section>
  )
}

function Products() {
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-20">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="rounded-2xl border border-gray-100 bg-gray-50 p-6 space-y-4">
          <div className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center">
            <BarChart3 size={20} className="text-white" />
          </div>
          <div>
            <p className="font-bold text-base">Distri<span className="text-rose-600">Hub</span> ERP</p>
            <p className="text-sm text-gray-600 mt-1 leading-relaxed">
              Full business management: inventory, POS, procurement, payroll, accounting, and more.
            </p>
          </div>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900 hover:underline"
          >
            Sign in <ArrowRight size={14} />
          </Link>
        </div>

        <div className="rounded-2xl border border-rose-100 bg-rose-50 p-6 space-y-4 relative overflow-hidden">
          <div className="absolute top-3 right-3 text-xs font-semibold bg-rose-600 text-white px-2 py-0.5 rounded-full">
            New
          </div>
          <div className="w-10 h-10 rounded-xl bg-rose-600 flex items-center justify-center">
            <ShoppingBag size={20} className="text-white" />
          </div>
          <div>
            <p className="font-bold text-base">Distri<span className="text-rose-600">Market</span></p>
            <p className="text-sm text-rose-900/70 mt-1 leading-relaxed">
              Your own storefront in your customers' pockets. Let them order directly from you, anytime.
            </p>
          </div>
          <Link
            to="/register"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-rose-700 hover:underline"
          >
            Set up your store <ArrowRight size={14} />
          </Link>
        </div>

        <div className="rounded-2xl border border-blue-100 bg-blue-50 p-6 space-y-4">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
            <Smartphone size={20} className="text-white" />
          </div>
          <div>
            <p className="font-bold text-base">Distri<span className="text-rose-600">Hub</span> Field</p>
            <p className="text-sm text-blue-900/70 mt-1 leading-relaxed">
              For field agents and warehouse staff. Take orders, record deliveries, and sync with your ERP on the go.
            </p>
          </div>
          <p className="text-xs text-blue-700 font-medium">
            Available on the employee portal
          </p>
        </div>
      </div>
    </section>
  )
}

function HowItWorks() {
  const steps = [
    {
      number: '01',
      icon: ClipboardList,
      title: 'Register your business',
      body: 'Sign up free in minutes. Add your products, branches, and team members — no technical setup needed.',
    },
    {
      number: '02',
      icon: BarChart3,
      title: 'Go live from day one',
      body: 'Start selling at the counter, dispatching field agents, and managing stock across all your locations immediately.',
    },
    {
      number: '03',
      icon: TrendingUp,
      title: 'Grow with real data',
      body: 'DistriAI surfaces the insights that matter: what is selling, what is not, where your money is going, and what to do next.',
    },
  ]

  return (
    <section className="border-t border-gray-100 bg-gray-50 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="text-center mb-14">
          <h2 className="text-2xl sm:text-3xl font-bold">Up and running in minutes</h2>
          <p className="mt-3 text-gray-600 max-w-xl mx-auto">
            No consultants. No lengthy onboarding. Just sign up and start running your business.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map(({ number, icon: Icon, title, body }) => (
            <div key={number} className="relative">
              <div className="text-6xl font-black text-gray-300 leading-none mb-4">{number}</div>
              <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center mb-3">
                <Icon size={18} className="text-rose-600" />
              </div>
              <h3 className="font-bold text-base mb-2">{title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Features() {
  const columns = [
    {
      icon: Package,
      title: 'Run your operations',
      items: [
        'Stock control across all branches',
        'Point of sale and invoicing',
        'Procurement and LPO management',
        'Supplier and customer accounts',
        'Transfer requests between branches',
      ],
    },
    {
      icon: Users,
      title: 'Manage your people',
      items: [
        'Staff records and payroll',
        'Leave tracking and approvals',
        'Field agent GPS and check-ins',
        'Role-based access per staff member',
        'Sales partner commission tracking',
      ],
    },
    {
      icon: Zap,
      title: 'Grow with intelligence',
      items: [
        'DistriAI business insights',
        'Sales analytics and reports',
        'Distri' + 'Market consumer storefront',
        'Flash sales and group buys',
        'Live shopping for your customers',
      ],
    },
  ]

  return (
    <section className="py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="text-center mb-14">
          <h2 className="text-2xl sm:text-3xl font-bold">Everything in one place</h2>
          <p className="mt-3 text-gray-600 max-w-xl mx-auto">
            No more switching between apps. No more spreadsheets. No more WhatsApp orders you can not track.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {columns.map(({ icon: Icon, title, items }) => (
            <div key={title} className="rounded-2xl border border-gray-100 p-6 space-y-4">
              <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center">
                <Icon size={18} className="text-rose-600" />
              </div>
              <h3 className="font-bold text-base">{title}</h3>
              <ul className="space-y-2">
                {items.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle2 size={15} className="text-rose-500 mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function DistriMarketSpotlight() {
  return (
    <section className="bg-rose-600 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full">
              New
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
              Let your customers order<br />directly from you.
            </h2>
            <p className="text-rose-100 leading-relaxed">
              Distri<span className="font-bold text-white">Market</span> puts your products in front of buyers near you.
              Flash sales, group buys, live shopping — your own branded storefront in their pocket.
              Orders flow straight into your ERP. No middleman.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 border border-white/40 text-white font-medium text-sm px-5 py-3 rounded-xl hover:bg-white/10 transition-colors"
              >
                Set up your store
                <ArrowRight size={15} />
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: ShoppingBag, label: 'Live shopping', desc: 'Sell in real time to an engaged audience' },
              { icon: Zap, label: 'Flash sales', desc: 'Move stock fast with time-limited deals' },
              { icon: Users, label: 'Group buys', desc: 'Let customers pool orders for better prices' },
              { icon: MapPin, label: 'Shop local', desc: 'Buyers discover stores closest to them first' },
            ].map(({ icon: Icon, label, desc }) => (
              <div key={label} className="bg-white/10 rounded-xl p-4 space-y-2">
                <Icon size={18} className="text-white" />
                <p className="text-white font-semibold text-sm">{label}</p>
                <p className="text-rose-100 text-xs leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

function Industries() {
  const list = [
    'FMCG distributors',
    'Pharmacies and chemists',
    'Hardware and building supplies',
    'Agri-dealers',
    'Fast food and restaurants',
    'General retail and supermarkets',
    'Electronics and appliances',
    'Cosmetics and beauty',
  ]

  return (
    <section className="border-t border-gray-100 bg-gray-50 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold mb-3">Built for businesses that move products</h2>
        <p className="text-gray-600 mb-10 max-w-xl mx-auto">
          Whether you run one shop or a multi-branch distribution network, Distri<span className="text-rose-600">Hub</span> scales with you.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          {list.map((industry) => (
            <span
              key={industry}
              className="bg-white border border-gray-200 text-gray-600 text-sm font-medium px-4 py-2 rounded-full"
            >
              {industry}
            </span>
          ))}
        </div>
      </div>
    </section>
  )
}

function CTA() {
  return (
    <section className="py-20">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold">Ready to take control of your business?</h2>
        <p className="mt-3 text-gray-600">
          Join distributors and retailers across Kenya already running on Distri<span className="text-rose-600">Hub</span>.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/register"
            className="flex items-center gap-2 bg-rose-600 text-white font-semibold px-6 py-3 rounded-xl hover:bg-rose-700 transition-colors text-sm"
          >
            Create your account
            <ArrowRight size={16} />
          </Link>
          <a
            href="mailto:info@distrihub.co.ke"
            className="flex items-center gap-2 border border-gray-200 text-gray-700 font-medium px-6 py-3 rounded-xl hover:bg-gray-50 transition-colors text-sm"
          >
            Talk to us
          </a>
        </div>
        <p className="mt-4 text-xs text-gray-500">Free to start. No credit card. No contract.</p>
      </div>
    </section>
  )
}

function Footer() {
  return (
    <footer className="border-t border-gray-100 py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-400">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="DistriHub" className="w-5 h-5 object-contain" />
          <span>© {new Date().getFullYear()} Distri<span className="text-rose-600">Hub</span>. All rights reserved.</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="mailto:info@distrihub.co.ke" className="hover:text-gray-600">info@distrihub.co.ke</a>
          <Link to="/privacy" className="hover:text-gray-600">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-gray-600">Terms &amp; Conditions</Link>
        </div>
      </div>
    </footer>
  )
}
